import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { UserPlus, Check, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadCaptureForm() {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (!mobile.trim() || mobile.trim().length < 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { name: name.trim(), mobile: mobile.trim() });
      if (window.gtag) {
        window.gtag('event', 'sign_up', { event_category: 'lead', event_label: 'lead_form_submit' });
      }
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lead-capture" data-testid="lead-capture-section" className="py-12 md:py-20 bg-green-50">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <h2
          data-testid="lead-capture-headline"
          className="text-3xl md:text-4xl font-bold text-slate-900 text-center"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Register Now — <span className="text-green-700">Start Free</span>
        </h2>
        <p className="mt-2 text-center text-slate-600 text-sm md:text-base">
          Enter your details and we will send you the app link and updates on WhatsApp.
        </p>

        <Card className="mt-8 max-w-md mx-auto bg-white border border-green-100 shadow-md">
          <CardContent className="p-6">
            {submitted ? (
              <div data-testid="lead-capture-success" className="text-center py-6">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-green-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Thank You!
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  We have received your details. You will get the app link on WhatsApp shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="lead-name" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Your Name
                  </label>
                  <Input
                    id="lead-name"
                    data-testid="lead-name-input"
                    type="text"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12 bg-white border-slate-200 focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label htmlFor="lead-mobile" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Mobile Number
                  </label>
                  <Input
                    id="lead-mobile"
                    data-testid="lead-mobile-input"
                    type="tel"
                    placeholder="Enter 10-digit mobile number"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    maxLength={10}
                    className="h-12 bg-white border-slate-200 focus:border-green-500 focus:ring-green-500"
                  />
                </div>

                {error && (
                  <p data-testid="lead-form-error" className="text-sm text-red-600">
                    {error}
                  </p>
                )}

                <Button
                  data-testid="lead-form-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-green-700 hover:bg-green-800 text-white rounded-lg font-semibold text-base shadow-md shadow-green-900/20"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <UserPlus className="mr-2 w-4 h-4" />
                      Register Now
                    </>
                  )}
                </Button>

                <p className="text-center text-xs text-slate-400">
                  Your information is safe with us. No spam.
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
